import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

class MlGenerateText extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.mlgeneratetext',
        group: 'Generative AI',
        title: 'ML Generate Text',
        description: `
      This component adds a new column with the result of calling the Generate Text function with prompts from the input table.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'model',
          type: WorkflowComponentParamType.String,
          title: 'Model',
          validation:
            '^[a-zA-Z_][a-zA-Z0-9_-]*\\.[a-zA-Z_][a-zA-Z0-9_-]*\\.[a-zA-Z_][a-zA-Z0-9_-]*$',
          allowExpressions: false
        },
        {
          name: 'prompt',
          type: WorkflowComponentParamType.Column,
          title: 'Prompt column',
          parent: 'source',
          dataType: [ColumnDataType.String]
        },
        {
          name: 'maxoutputtokens',
          type: WorkflowComponentParamType.Number,
          title: 'Max output tokens',
          default: 256,
          min: 1
        },
        {
          name: 'temperature',
          type: WorkflowComponentParamType.Number,
          title: 'Temperature',
          default: 0.2,
          min: 0
        },
        {
          name: 'topp',
          type: WorkflowComponentParamType.Number,
          title: 'Top P',
          default: 0.8,
          min: 0
        },
        {
          name: 'topk',
          type: WorkflowComponentParamType.Number,
          title: 'Top K',
          default: 40,
          min: 0
        }
      ]
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '3'
  }

  getInnerSql({ params, connection, codeType }: WorkflowComponentCodeParams) {
    const preparedParams = this._prepareParameters(params)
    const dryRun = codeType === WorkflowCodeType.DryRun
    if (dryRun) {
      return dedent`
            SELECT
              *,
              'dummy' AS ml_generate_text_result,
              'dummy' AS ml_generate_text_content
            FROM
              ${connection.quoteName(preparedParams.source as string)}
        `
    } else {
      if (preparedParams.prompt === 'prompt') {
        return dedent`
        SELECT
          * EXCEPT (ml_generate_text_status),
          TRIM(STRING(
              ml_generate_text_result.candidates[0].content.parts[0].text
          )) AS ml_generate_text_content
        FROM
          ML.GENERATE_TEXT(
              MODEL ${connection.quoteName(preparedParams.model as string)},
              (
                  SELECT *
                  FROM ${connection.quoteName(preparedParams.source as string)}
              ),
              STRUCT(
                  ${preparedParams.temperature} AS temperature,
                  ${preparedParams.maxoutputtokens} AS max_output_tokens,
                  ${preparedParams.topp} AS top_p,
                  ${preparedParams.topk} AS top_k
              )
          )
        `
      } else {
        return dedent`
        SELECT
          * EXCEPT (prompt, ml_generate_text_status),
          TRIM(STRING(
              ml_generate_text_result.candidates[0].content.parts[0].text
          )) AS ml_generate_text_content
        FROM
          ML.GENERATE_TEXT(
              MODEL ${connection.quoteName(preparedParams.model as string)},
              (
                  SELECT *, ${preparedParams.prompt} AS prompt
                  FROM ${connection.quoteName(preparedParams.source as string)}
              ),
              STRUCT(
                  ${preparedParams.temperature} AS temperature,
                  ${preparedParams.maxoutputtokens} AS max_output_tokens,
                  ${preparedParams.topp} AS top_p,
                  ${preparedParams.topk} AS top_k
              )
          )
        `
      }
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { MlGenerateText }
